

import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FormGroup, Label, Alert } from 'reactstrap';
import { Link } from 'react-router-dom';
// import axios from 'axios';
import { useAuth } from 'helpers/authContext';
import { formatMB, uploadVideoToIPFS, detectVideoOrientation } from 'helpers/videoUpload';
import { useToast } from './ui/use-toast';
import { clearUploadState, getUploadState } from 'helpers/UploadState';
import { uploadManager } from 'helpers/UploadContext'; 
import { getVideo } from 'helpers/UploadDB';
// import { upload_video_chunked } from 'services/db-service';

type FormData = {
  video: File | null;
  username: string;
};

interface Props { 
    link:string
    setLink:Function;
    IpfsAccount:any;
    uploading?: boolean;
    setUploading?: (state: boolean) => void; 
    paused?: boolean;
    setPaused?: (state: boolean) => void;
    setOrientation?: any;
    cancelUploadState?:boolean;
    onFileSelected?: (file: File) => void;
}

const DAYS_FOR_RESET = parseInt(process.env.DAYS_FOR_RESET) || 30;
const IPFSUploader: React.FC<Props> = ({link,setLink, IpfsAccount, uploading, setUploading,paused,setPaused, setOrientation, cancelUploadState, onFileSelected}) => {
    const { toast } = useToast();
    const { register, setValue } = useForm<FormData>();
    const [videoName, setVideoName] = useState('');
    const [pausedReason, setPausedReason] = useState("");
    const [AvailableQuota, setAvailableQuota] = useState(5000);
    const [UsedQuota, setUsedQuota] = useState(0);
    const { user } = useAuth();
    const [error, setError] = useState<string | null>(null);
    const [progress, setProgress] = useState(0);
    const [loaded, setLoaded] = useState(0);
    const [size, setSize] = useState(0);
    const [uploadResult, setUploadResult] = useState<string | null>(link?"✅ Uploaded!": null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const resetUploader = () => {
        setProgress(0);
        setLoaded(0);
        setPaused(false);
        setPausedReason("");
        setUploadResult(null);
        setVideoName("");
        setSize(0);
    };
    useEffect(() => {
        if (cancelUploadState) {
          setProgress(0);
          setUploading && setUploading(false); 
        }
    }, [cancelUploadState]);
    useEffect(() => {
        register('video');
        register('username');
        setValue('username', user);
    }, [register]);
    useEffect(() => {
        if (!uploading && false) {
            resetUploader();
        }
    }, [uploading]); 
    useEffect(() => {
        const state = getUploadState();
        if (!state) return;
        let disposed = false;
        let completed = false;
        let checker: ReturnType<typeof setInterval> | null = null;
        const stopChecking = () => {
            if (checker !== null) clearInterval(checker);
            checker = null;
        };
        getVideo(state.uploadId).then(file => {
            if (!disposed && file) setSize(file.size);
        }).catch(() => {});
        const checkCompletion = () => {
            if (disposed || completed) return;
            const current = getUploadState();
            if (!current || current.uploadId !== state.uploadId) {
                stopChecking();
                return;
            }
            if (!current.cid || !current.url) return;
            completed = true;
            stopChecking();
            setLink(current.url);
            setUploadResult(`✅ Uploaded! CID: ${current.cid}`);
            toast({
                title: "✅ Uploaded!",
                description: `You can see the content using this CID: ${current.cid}`,
                variant: "wild_green"
            });
            clearUploadState();
            setUploading && setUploading(false);
            setProgress(0);
        };
        const unsubscribe = uploadManager.subscribe(state.uploadId, data => {
            if (disposed || completed) return;
            if (data.error) {
                stopChecking();
                setPaused && setPaused(true);
                setPausedReason(data.error);
                setUploading && setUploading(false);
                setUploadResult(`❌ Upload failed: ${data.error}`);
                return;
            }
            if (typeof data.progress === 'number') setProgress(data.progress);
            if (typeof data.loaded === 'number') setLoaded(data.loaded);
            if (data.progress === 100 && checker === null) {
                checker = setInterval(checkCompletion, 3000);
            }
        });
        return () => {
            disposed = true;
            stopChecking();
            unsubscribe && unsubscribe();
        };
    }, []);
    useEffect(() => {
        const validFiles = Array.isArray(IpfsAccount.files)
            ? IpfsAccount.files.filter(item => IpfsAccount.plan.includes("plus") ? Number(item.days_ago) <= DAYS_FOR_RESET:true)
            : []; 
        const totalSize = Array.isArray(validFiles)
        ? validFiles.reduce((acc, item) => acc + Number(item.size || 0), 0)
        : 0;
        const available_quota = (IpfsAccount.quota*1e6)-totalSize; 
        setAvailableQuota(available_quota);
        setUsedQuota(totalSize); 
    }, [IpfsAccount]);
    const validateFile = (file: File): boolean => {
        const validTypes = ['video/mp4', 'video/quicktime'];
        const maxSize = IpfsAccount.max_size * 1024 * 1024; 
        console.log(`available: (${AvailableQuota/1e6} mb/${IpfsAccount.quota} mb) Plan: ${IpfsAccount.plan}`);
        
        if (!validTypes.includes(file.type)) {
        setError('Only MP4 or MOV files are allowed.');
        return false;
        }
        if (file.size > maxSize) {
        setError('File exceeds the '+IpfsAccount.max_size+' MB limit per file.');
        return false;
        }
        if (AvailableQuota < file.size) {
            setError(`You have exceed your uploading quota. Available: ${AvailableQuota} File size: ${file.size}`);
            return false;
        }
        setError(null);
        return true;
    };

    const handleFile = async (file: File) => {
        if (validateFile(file)) {
            setValue('video', file);
            setVideoName(file.name);
            setSize(file.size);
            setUploadResult(null);
            
            if (onFileSelected) onFileSelected(file);

            // Detect orientation
            if (setOrientation) {
                const orientation = await detectVideoOrientation(file);
                setOrientation(orientation);
            }

            if (!file) {
                setError('Please select a valid video file.');
                return;
            }
            const state = getUploadState();

            if (state && uploading) {
                setError("Another upload is in progress");
                return;
            }
            handleUpload(file);
        }
    };

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    };

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => { 
        const file = e.target.files?.[0]; 
        
        if (file) handleFile(file);
    };

    const handleUpload = async (file: File) => {
        const formData = new FormData();
        formData.append('video', file);
        formData.append('username', user); 
        try { 
            setUploading && setUploading(true);
            console.log("Uploading started...");
            let res:any = await uploadVideoToIPFS({file, user, setProgress, setPaused, setPausedReason, setLoaded});
            // A paused or cancelled transfer has no completed media result.
            if (!res) return;
            const { cid, url } = res.data || {};
            if (!cid || !url) {
                throw new Error("The server did not return a completed video. Please retry the upload.");
            }
            setLink(url);
            setUploadResult(`✅ Uploaded! CID: ${cid}`);
            toast({
                title: "✅ Uploaded!",
                description: `You can see the content using this CID: ${cid}`,
                variant:"wild_green"
            }); 
            // setOrientation && setOrientation("clip");
        } catch (err: any) {
            const message = err.response?.data?.message || err.response?.data?.error
                || err.message || "Unable to upload the video. Please retry.";
            setUploadResult(`❌ Upload failed: ${message}`);
            toast({
                title: "❌ Upload failed:",
                description: message,
                variant:"rose_red"
            }); 
        } finally {
            setUploading && setUploading(false);
            console.log("Uploading started...");
            setProgress(0);
        }
    }; 

    useEffect(() => {
        if (error) {
            toast({
                title: "Ops! Something have failed.",
                description: error,
                variant:"rose_red"
            }); 
        }
    }, [error]); 

    return (
        <div >
        <FormGroup>
            {((UsedQuota / (IpfsAccount.quota * 1e6)) > 0.75 && (UsedQuota / (IpfsAccount.quota * 1e6)) < 0.98) && (
                <Alert color='primary'>
                    You're over 75% of your storage quota. Upgrade your plan to continue uploading files without limitations. <Link to="/settings">Learn more here.</Link>
                </Alert>
            )}
            {((UsedQuota / (IpfsAccount.quota * 1e6)) >= 0.98) && (
                <Alert color='danger'>
                    Your storage is full. Upgrade your plan to continue uploading. <Link to="/settings">Learn more here.</Link>
                </Alert>
            )}
            <Label>1. Upload your video:</Label>
            <div
                className="video-upload-box fancy-input p-3 text-center"
                onDrop={onDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => {
                    console.log('onclick');
                    
                    fileInputRef.current?.click()
                }}
            >
            <input
                type="file"
                accept=".mp4,.mov"
                style={{ display: 'none' }}
                id="videoInput"
                name="videoInput"
                ref={fileInputRef} 
                onChange={onFileChange}
            />
            <div  style={{ cursor: 'pointer' }}>
                <strong><i className="fas fa-cloud-arrow-up" /> Drag and drop a video file here</strong> or <span className="text-primary">click to select</span>
                <div className="icon-large">
                    <i className="fas fa-file-import"  /> 
                </div>
                
            </div>

            {videoName && (
                <div className="mt-3">
                    <p className="text-success mb-2">Selected: {videoName}</p>
                </div>
            )}
            {error && <p className="mt-2 text-danger">{error}</p>}
            </div>
        </FormGroup>

        {(uploading && progress > 0 && !paused) && (
            <div className="relative w-100">
                <div className="post-way empty w-100"> 
                </div>
                <div className="post-way green" style={{position:"absolute",top:0,width:progress+"%"}}> 
                    
                </div> 
                <div className="w-100 mt-3 text-center">
                    {progress == 100?"Processing...":progress+"%"} ({formatMB(loaded)} MB/{formatMB(size)} MB)
                </div>
            </div>
        )}
        {(progress > 0 && paused) && (
            <div className="relative w-100 color-red">
                <b>{pausedReason}</b>
            </div>
        )}
        {uploadResult && (
            <Alert fade={true} className="mt-3" color={uploadResult.startsWith('✅') ? 'success' : 'danger'}>
                {uploadResult}
                {uploadResult.startsWith('✅') && (
                <div className="mt-2">
                    <a href={link} target="_blank" rel="noopener noreferrer">
                    View on IPFS ↗
                    </a>
                </div>
                )}
            </Alert>
            )}


        </div>
    );
};

export default IPFSUploader; 

